import React, { useCallback, useMemo } from 'react';
import type { FC } from 'react';
import type { SelectGroup, SelectOption } from '@wix/cli-ui-kit';
import { SelectGroupsInput, InputState, TextInput } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import type { SupportedServicePlugin } from '@wix/cli-app-definitions';
import { ComponentType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { useWixCliAppBi } from '../../../bi/index.js';
import { useWizardStep } from '../Wizard.js';
import {
  createServicePluginValidation,
  useValidation,
} from '../../../dev-center/components/validations/index.js';
import { useExtensions } from '../../extensions/useExtensions.js';

export const SelectServicePluginStep: FC<{
  typeOption?: SelectOption<SupportedServicePlugin>;
  onSubmit: (value: SelectOption<SupportedServicePlugin>) => void;
}> = ({ onSubmit, typeOption }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { isActive, goToNextStep } = useWizardStep();

  const generateServicePluginOptions: Array<
    SelectGroup<SupportedServicePlugin>
  > = useMemo(
    () => [
      {
        title: t('generate_command.service_plugin.groups.stores'),
        items: [
          {
            title: t('generate_command.service_plugin.ecom_additional_fees'),
            value: ComponentType.ECOM_ADDITIONAL_FEES,
          },
          {
            title: t('generate_command.service_plugin.ecom_shipping_rates'),
            value: ComponentType.ECOM_SHIPPING_RATES,
          },
          {
            title: t('generate_command.service_plugin.ecom_discount_triggers'),
            value: ComponentType.ECOM_DISCOUNTS_TRIGGER,
          },
          {
            title: t('generate_command.service_plugin.ecom_validations'),
            value: ComponentType.ECOM_VALIDATIONS,
          },
          {
            title: t('generate_command.service_plugin.ecom_payment_settings'),
            value: ComponentType.ECOM_PAYMENT_SETTINGS,
          },
          {
            title: t(
              'generate_command.service_plugin.ecom_gift_cards_provider'
            ),
            value: ComponentType.GIFT_CARDS_PROVIDER,
          },
          {
            title: t(
              'generate_command.service_plugin.ecom_recommendations_provider'
            ),
            value: ComponentType.ECOM_RECOMMENDATIONS_PROVIDER,
          },
        ],
      },
      {
        title: t('generate_command.service_plugin.groups.billing'),
        items: [
          {
            title: t('generate_command.service_plugin.premium_custom_charges'),
            value: ComponentType.PREMIUM_CUSTOM_CHARGES,
          },
        ],
      },
      {
        title: t('generate_command.service_plugin.groups.data'),
        items: [
          {
            title: t(
              'generate_command.service_plugin.external_database_provider'
            ),
            value: ComponentType.EXTERNAL_DATABASE_PROVIDER,
          },
        ],
      },
      {
        title: t('generate_command.service_plugin.groups.marketing'),
        items: [
          {
            title: t('generate_command.service_plugin.seo_keyword_suggestions'),
            value: ComponentType.SEO_KEYWORDS_SUGGESTIONS,
          },
        ],
      },
      {
        title: t('generate_command.service_plugin.groups.bookings'),
        items: [
          {
            title: t('generate_command.service_plugin.staff_sorting_provider'),
            value: ComponentType.STAFF_SORTING_PROVIDER,
          },
          //     {
          //       title: t(
          //         'generate_command.service_plugin.bookings_pricing_provider'
          //       ),
          //       value: ComponentType.BOOKINGS_PRICING_PROVIDER,
          //     },
        ],
      },
    ],
    [t]
  );

  const selectSupportedServicePlugin = useCallback(
    (supportedServicePlugin: SelectOption<SupportedServicePlugin>) => {
      bi.cliFlowStepAnswered({
        question: t('generate_command.service_plugin.choose_type'),
        questionKey: 'generate_command.service_plugin.choose_type',
        answer: supportedServicePlugin.title,
      });
      onSubmit(supportedServicePlugin);
      goToNextStep();
    },
    [bi, t, onSubmit, goToNextStep]
  );

  return (
    <SelectGroupsInput<SupportedServicePlugin>
      value={
        !isActive && typeOption
          ? InputState.Submitted(typeOption)
          : InputState.NotSubmitted(typeOption)
      }
      label={t('generate_command.service_plugin.choose_type')}
      groups={generateServicePluginOptions}
      onSubmit={selectSupportedServicePlugin}
      maxHeight={14}
    />
  );
};

export const ChooseNameStep: FC<{
  name?: string;
  type?: SupportedServicePlugin;
  onSubmit: (value: string) => void;
}> = ({ onSubmit, type, name }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { getExtensions } = useExtensions();
  const { isActive, goToNextStep } = useWizardStep();

  const validateServicePluginFolder = useValidation(
    createServicePluginValidation(getExtensions('ServicePlugin'), type)
  );

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.service_plugin.choose_name')}
      placeholder={
        name ?? t('generate_command.service_plugin.name_placeholder')
      }
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.service_plugin.choose_name'),
          questionKey: 'generate_command.service_plugin.choose_name',
          answer: value,
          additionalInfo: { servicePluginType: type },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateServicePluginFolder}
    />
  );
};
